import { type FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useTopicType } from "~/components/topic/hooks";
import type { topicDescriptionTopic$key } from "~/queries/__generated__/topicDescriptionTopic.graphql";

import { createDOMElement } from "./helpers";
import {
  Container,
  Description,
  DescriptionContainer,
  ImageIcon,
  ShowButton,
  Title,
  TitleContainer,
} from "./styles";

type Props = {
  className?: string;
  reference: topicDescriptionTopic$key;
};

export const TopicDescription: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const topic = useFragment(
    graphql`
      fragment topicDescriptionTopic on Topic {
        ...topicHeaderRelatedLinksTopic
        ...entityFollowButtonBase
        ...hooksTopic
        mobileImage: image(type: MOBILE_APP_IMAGE) {
          alt
          title
          style(filter: { style: "300x300" }) {
            url
          }
        }
        name
        description {
          json
          text
        }
      }
    `,
    reference_,
  );

  const iconImage = topic.mobileImage;

  const { currentType, hasSponsor } = useTopicType(topic);
  const isGeneralTopic = currentType === "general";
  const isShowImageIcon = !isGeneralTopic && !hasSponsor;

  const descriptionContainerReference = useRef<HTMLDivElement>(null);
  const descriptionReference = useRef<HTMLDivElement>(null);
  const [isEllipsis, setIsEllipsis] = useState(true);
  const [isShowFullProfile, setIsShowFullProfile] = useState(false);
  const isShowButton = topic.description?.json && !isShowFullProfile && isEllipsis;

  const filteredText = topic.description?.json?.filter(item =>
    item?.children?.find(item => item.type !== "iframe"),
  );

  const withText = (filteredText?.length ?? 0) > 0;

  const getRawDescriptionHeight = useCallback(() => {
    const descriptionContainer = descriptionContainerReference.current;
    if (descriptionContainer) {
      const rawDescription = document.createElement("div");
      rawDescription.style.lineHeight = "19.6px";
      rawDescription.style.fontSize = "14px";
      rawDescription.textContent = "";
      filteredText?.forEach(node => {
        rawDescription.append(createDOMElement(node));
      });

      descriptionContainer.append(rawDescription);
      const height = rawDescription.offsetHeight;

      rawDescription.remove();

      return height;
    }

    return 0;
  }, [filteredText]);

  useEffect(() => {
    if (descriptionReference.current) {
      descriptionReference.current.textContent = "";
      filteredText?.forEach(node => descriptionReference.current?.append(createDOMElement(node)));
    }
    return;
  }, [filteredText]);

  useEffect(() => {
    if (descriptionContainerReference.current) {
      const maxHeight = 60;
      setIsEllipsis(getRawDescriptionHeight() > maxHeight);
    }
    return;
  }, [getRawDescriptionHeight]);

  if (!withText) return null;

  return (
    <Container className={className}>
      <TitleContainer>
        {isShowImageIcon && iconImage?.style?.url && (
          <ImageIcon
            $aspectRatio="1"
            alt={iconImage.alt ?? iconImage.title ?? ""}
            lazyload={false}
            src={iconImage.style.url}
          />
        )}

        <Title>{topic.name}</Title>
      </TitleContainer>
      <DescriptionContainer ref={descriptionContainerReference}>
        <Description
          $isShowFullProfile={isShowFullProfile || !isEllipsis}
          ref={descriptionReference}
        >
          {topic.description?.text}
        </Description>
      </DescriptionContainer>
      {isShowButton && (
        <ShowButton onClick={() => setIsShowFullProfile(true)}>Show full profile</ShowButton>
      )}
    </Container>
  );
};

TopicDescription.displayName = "TopicDescription";
